import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Déconnexion automatique après inactivité (15 minutes). Ajustable ici.
const IDLE_MS = 15 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=auth
  const [loading, setLoading] = useState(true);
  const [idleLeft, setIdleLeft] = useState(null); // secondes restantes avant déconnexion (null = pas d'alerte)
  const warnTimer = useRef(null);
  const countdown = useRef(null);
  const warnRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("cd_authed", "1");
      return data;
    } catch {
      setUser(false);
      localStorage.removeItem("cd_authed");
      return false;
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    setUser(data);
    localStorage.setItem("cd_authed", "1");
    return data;
  };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password });
    setUser(data);
    return data;
  };

  const logout = useCallback(async (reason) => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    localStorage.removeItem("cd_authed");
    if (reason === "idle") {
      toast.info("Vous avez été déconnecté après une période d'inactivité. Reconnectez-vous pour continuer.");
    }
  }, []);

  // Déconnexion automatique après inactivité, avec avertissement 60 s avant.
  const WARN_S = 60;
  const clearIdle = useCallback(() => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (countdown.current) clearInterval(countdown.current);
    warnTimer.current = null; countdown.current = null;
  }, []);

  const arm = useCallback(() => {
    clearIdle();
    setIdleLeft(null);
    warnRef.current = false;
    warnTimer.current = setTimeout(() => {
      warnRef.current = true;
      setIdleLeft(WARN_S);
      countdown.current = setInterval(() => {
        setIdleLeft((s) => {
          if (s === null) return null;
          if (s <= 1) { clearIdle(); warnRef.current = false; logout("idle"); return null; }
          return s - 1;
        });
      }, 1000);
    }, Math.max(IDLE_MS - WARN_S * 1000, 0));
  }, [clearIdle, logout]);

  const stayConnected = useCallback(async () => {
    warnRef.current = false;
    setIdleLeft(null);
    try { await api.get("/auth/me"); } catch {}
    arm();
  }, [arm]);

  useEffect(() => {
    if (!user || user === false) { clearIdle(); warnRef.current = false; setIdleLeft(null); return; }
    const onActivity = () => { if (!warnRef.current) arm(); }; // pendant l'alerte : on ne réinitialise pas automatiquement
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    arm();
    return () => {
      clearIdle();
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user, arm, clearIdle]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, changePassword, refresh, formatApiError }}>
      {children}
      {idleLeft !== null && user && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm" data-testid="idle-warning-overlay">
          <div className="w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-200 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <span className="text-2xl">⏳</span>
            </div>
            <h3 className="text-lg font-bold text-slate-900">Votre session va expirer</h3>
            <p className="mt-2 text-sm text-slate-600">
              Pour votre sécurité, vous allez être déconnecté dans{" "}
              <span className="font-bold text-teal-600" data-testid="idle-countdown">{idleLeft}</span> secondes pour cause d'inactivité.
            </p>
            <p className="mt-1 text-xs text-slate-400">Vos activités non enregistrées sont conservées en brouillon.</p>
            <button
              data-testid="idle-stay-logged"
              onClick={stayConnected}
              className="mt-5 w-full rounded-xl bg-teal-600 px-4 py-3 font-semibold text-white transition hover:bg-teal-700"
            >
              Rester connecté
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
